import type { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import Meta from '@/components/Meta';
import NationalProjects from '@/components/national-projects/NationalProjects';
import {
  createUnavailableNationalProjectsSnapshot,
  fetchNationalProjects,
  type NationalProjectsSnapshot,
} from '@/lib/nationalProjects';
import { createNationalProjectsSample } from '@/lib/nationalProjectsSample';

type NationalProjectsPageProps = {
  initialSnapshot: NationalProjectsSnapshot;
};

export const NATIONAL_PROJECTS_REVALIDATE_SECONDS = 300;

export const getStaticProps: GetStaticProps<NationalProjectsPageProps> = async () => {
  try {
    const initialSnapshot = await fetchNationalProjects({ signal: AbortSignal.timeout(8_000) });
    return {
      props: { initialSnapshot },
      revalidate: NATIONAL_PROJECTS_REVALIDATE_SECONDS,
    };
  } catch {
    return {
      props: {
        initialSnapshot: createUnavailableNationalProjectsSnapshot(
          'The source-backed national register is temporarily unavailable.',
        ),
      },
      revalidate: 60,
    };
  }
};

export default function NationalProjectsPage({ initialSnapshot }: NationalProjectsPageProps) {
  const router = useRouter();
  const [sampleMode, setSampleMode] = useState(false);
  useEffect(() => {
    if (!router.isReady || process.env.NODE_ENV === 'production') return;
    setSampleMode(router.query.sample === '1' || /(?:\?|&)sample=1(?:&|$)/.test(router.asPath));
  }, [router.asPath, router.isReady, router.query.sample]);
  const sampleSnapshot = useMemo(
    () => sampleMode ? createNationalProjectsSample() : undefined,
    [sampleMode],
  );

  return (
    <>
      <Meta
        title="National Projects | CityScope"
        description="Follow major Scottish development projects across council boundaries, with official planning records separated clearly from CityScope inference."
        canonical="/national-projects"
        keywords="Scotland national projects, major developments Scotland, planning applications, infrastructure projects, CityScope"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'CityScope National Projects',
          description: 'A source-labelled register of major development projects spanning Scottish council areas.',
          url: 'https://www.city-scope.co.uk/national-projects',
        }}
      />
      <NationalProjects
        key={sampleMode ? 'sample' : 'live'}
        mode={sampleMode ? 'sample' : 'live'}
        initialSnapshot={sampleSnapshot ?? initialSnapshot}
      />
    </>
  );
}
